import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useSeasonal } from '../lib/useSeasonal';
import { THEMES } from '../lib/seasonalThemes';

const AppContext = createContext(null);

// Flat shipping — no discounts applied anywhere
export const SHIPPING = 25.00;

const AppProvider = ({ children }) => {
  const [accessGranted, setAccessGranted] = useState(() => localStorage.getItem('ep_access') === '1');
  const [ageAccepted, setAgeAccepted] = useState(() => localStorage.getItem('ep_age') === '1');
  const [theme, setTheme] = useState(() => localStorage.getItem('ep_theme') || 'light');
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ep_cart') || '[]'); } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [searchNonce, setSearchNonce] = useState(0);
  const [quickView, setQuickView] = useState(null);
  const runSearch = useCallback((q, cat) => { setSearch(q); setActiveCat(cat || 'all'); setSearchNonce(n => n + 1); }, []);

  const seasonal = useSeasonal();
  const forcedTheme = THEMES[seasonal?.theme]?.mode || null;
  const shownTheme = forcedTheme || theme;

  useEffect(() => {
    const root = document.documentElement;
    if (shownTheme === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
  }, [shownTheme]);
  useEffect(() => { localStorage.setItem('ep_theme', theme); }, [theme]);

  useEffect(() => { localStorage.setItem('ep_cart', JSON.stringify(cart)); }, [cart]);

  const grantAccess = () => { localStorage.setItem('ep_access', '1'); setAccessGranted(true); };
  const acceptAge = () => { localStorage.setItem('ep_age', '1'); setAgeAccepted(true); };
  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  const addToCart = useCallback((item) => {
    setCart(prev => {
      const existing = prev.find(p => p.id === item.id);
      if (existing) return prev.map(p => p.id === item.id ? { ...p, qty: p.qty + 1 } : p);
      return [...prev, { ...item, qty: 1 }];
    });
    toast.success('Product added to cart', {
      description: item.name,
      duration: 2200,
    });
  }, []);

  const removeFromCart = (id) => setCart(prev => prev.filter(p => p.id !== id));
  const updateQty = (id, qty) => setCart(prev => qty <= 0 ? prev.filter(p => p.id !== id) : prev.map(p => p.id === id ? { ...p, qty } : p));
  const clearCart = () => setCart([]);
  const syncPrices = useCallback((quotes) => {
    const map = Object.fromEntries((quotes || []).map(q => [String(q.id), Number(q.price)]));
    setCart(prev => {
      const changed = prev.some(p => map[String(p.id)] !== undefined && map[String(p.id)] !== Number(p.price));
      return changed ? prev.map(p => (map[String(p.id)] !== undefined ? { ...p, price: map[String(p.id)] } : p)) : prev;
    });
  }, []);

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const shipping = cart.length > 0 ? SHIPPING : 0;
  const total = +(subtotal + shipping).toFixed(2);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const openCheckout = () => { setCartOpen(false); setCheckoutOpen(true); };

  return (
    <AppContext.Provider value={{
      accessGranted, grantAccess, ageAccepted, acceptAge,
      theme: shownTheme, themeLocked: !!forcedTheme, toggleTheme,
      cart, addToCart, removeFromCart, updateQty, clearCart, syncPrices,
      subtotal, shipping, total, cartCount,
      cartOpen, setCartOpen,
      checkoutOpen, setCheckoutOpen, openCheckout,
      search, setSearch, activeCat, setActiveCat, searchNonce, runSearch, quickView, setQuickView,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export { AppProvider };
export const useApp = () => useContext(AppContext);
